import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

// Mock chat data
const MOCK_MESSAGES = [
  {
    id: '1',
    sender_id: 'ai-bot',
    sender_name: 'Helpdeck AI',
    content: 'สวัสดีค่ะ ฉันคือ Helpdeck AI Assistant พร้อมช่วยเหลือคุณ',
    created_at: '2026-04-20 10:30:00',
    is_own: false,
    read: true,
  },
  {
    id: '2',
    sender_id: 'ai-bot',
    sender_name: 'Helpdeck AI',
    content: 'เลือกเรื่องที่ต้องการช่วยเหลือได้เลยค่ะ',
    buttons: [
      { id: 'btn-1', label: 'เรื่อง IT', value: 'it_issue' },
      { id: 'btn-2', label: 'เรื่อง HR', value: 'hr_issue' },
      { id: 'btn-3', label: 'เรื่อง Account', value: 'account_issue' },
      { id: 'btn-4', label: 'เรื่องอื่นๆ', value: 'other' },
    ],
    created_at: '2026-04-20 10:31:00',
    is_own: false,
    read: true,
  },
  {
    id: '3',
    sender_id: 'customer-001',
    sender_name: 'You',
    content: 'เรื่อง IT',
    created_at: '2026-04-20 10:31:30',
    is_own: true,
    read: true,
  },
  {
    id: '4',
    sender_id: 'ai-bot',
    sender_name: 'Helpdeck AI',
    content: 'ปัญหา IT คืออะไรครับ?',
    buttons: [
      { id: 'btn-5', label: 'เข้า Internet ไม่ได้', value: 'no_internet' },
      { id: 'btn-6', label: 'ปัญหาฮาร์ดแวร์', value: 'hardware' },
      { id: 'btn-7', label: 'ปัญหา Software', value: 'software' },
      { id: 'btn-8', label: 'ปัญหา Password/Login', value: 'login' },
    ],
    created_at: '2026-04-20 10:32:00',
    is_own: false,
    read: true,
  },
];

export default function ChatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [messages, setMessages] = useState(MOCK_MESSAGES);
  const [inputValue, setInputValue] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage({
          file: file,
          preview: event.target?.result,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() && !selectedImage) return;

    const newMessage = {
      id: String(messages.length + 1),
      sender_id: user?.id,
      sender_name: user?.full_name,
      content: inputValue,
      image: selectedImage?.preview,
      created_at: new Date().toLocaleString('th-TH'),
      is_own: true,
      read: true,
    };

    setMessages([...messages, newMessage]);
    setInputValue('');
    setSelectedImage(null);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: String(messages.length + 2),
        sender_id: 'ai-bot',
        sender_name: 'Helpdeck AI',
        content:
          'ขอบคุณที่บอกเรื่องประเด็นนี้มาค่ะ ฉันกำลังวิเคราะห์ข้อมูลของคุณ แล้วจะช่วยแก้ไขให้เลวร้ายที่สุด',
        created_at: new Date().toLocaleString('th-TH'),
        is_own: false,
        read: false,
      };
      setMessages((prev) => [...prev, aiResponse]);
    }, 1000);
  };

  const handleButtonClick = (button, buttonLabel) => {
    // Add user's button click as a message
    const userMessage = {
      id: String(messages.length + 1),
      sender_id: user?.id,
      sender_name: user?.full_name,
      content: buttonLabel,
      created_at: new Date().toLocaleString('th-TH'),
      is_own: true,
      read: true,
    };

    setMessages([...messages, userMessage]);

    // Simulate AI response after button click
    setTimeout(() => {
      const aiResponse = {
        id: String(messages.length + 2),
        sender_id: 'ai-bot',
        sender_name: 'Helpdeck AI',
        content:
          'ขอบคุณที่เลือก ' +
          buttonLabel +
          ' ค่ะ ฉันกำลังประมวลผลข้อมูลของคุณเพื่อหาวิธีแก้ไขที่ดีที่สุด',
        created_at: new Date().toLocaleString('th-TH'),
        is_own: false,
        read: false,
      };
      setMessages((prev) => [...prev, aiResponse]);
    }, 800);
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateString.split(' ')[1];
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
    } catch {
      return dateString.split(' ')[0];
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/chat')}
            style={styles.backBtn}
            title="Back to chat list"
          >
            ← กลับ
          </button>
          <div style={styles.ticketInfo}>
            <h2 style={styles.ticketNumber}>Helpdeck AI Support</h2>
            <p style={styles.ticketStatus}>
              <span style={styles.statusBadge}>Online</span>
            </p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.agentInfo}>
            <div style={styles.agentAvatar}>A</div>
            <div>
              <p style={styles.agentName}>AI Assistant</p>
              <p style={styles.agentStatus}>พร้อมช่วยเหลือ</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div style={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>✉</p>
            <p style={styles.emptyTitle}>ไม่มีข้อความ</p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => {
              const prevMsg = messages[idx - 1];
              const showDate =
                !prevMsg ||
                formatDate(msg.created_at) !== formatDate(prevMsg.created_at);

              return (
                <div key={msg.id}>
                  {showDate && (
                    <div style={styles.dateSeperator}>
                      <span>{formatDate(msg.created_at)}</span>
                    </div>
                  )}
                  <div
                    style={{
                      ...styles.messageRow,
                      justifyContent: msg.is_own ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {!msg.is_own && (
                      <div style={styles.agentAvatarSmall}>
                        {msg.sender_name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </div>
                    )}
                    <div
                      style={{
                        ...styles.messageBubble,
                        ...(msg.is_own
                          ? styles.bubbleOwn
                          : styles.bubbleOther),
                      }}
                    >
                      {!msg.is_own && (
                        <p style={styles.senderName}>{msg.sender_name}</p>
                      )}
                      {msg.image && (
                        <img
                          src={msg.image}
                          alt="message-attachment"
                          style={styles.messageImage}
                        />
                      )}
                      <p style={styles.messageContent}>{msg.content}</p>
                      {msg.buttons && msg.buttons.length > 0 && (
                        <div style={styles.buttonGroup}>
                          {msg.buttons.map((btn) => (
                            <button
                              key={btn.id}
                              onClick={() => handleButtonClick(btn, btn.label)}
                              style={styles.quickButton}
                              title={btn.label}
                            >
                              {btn.label}
                            </button>
                          ))}
                        </div>
                      )}
                      <div style={styles.messageFooter}>
                        <span style={styles.messageTime}>
                          {formatTime(msg.created_at)}
                        </span>
                        {msg.is_own && (
                          <span style={styles.readStatus}>
                            {msg.read ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <form onSubmit={handleSendMessage} style={styles.inputContainer}>
        <div style={styles.inputAreaWrapper}>
          {selectedImage && (
            <div style={styles.imagePreview}>
              <div style={styles.imagePreviewItem}>
                <img src={selectedImage.preview} alt="preview" style={styles.previewImg} />
                <button
                  type="button"
                  onClick={() => setSelectedImage(null)}
                  style={styles.removeImageBtn}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
              <span style={styles.imageFileName}>{selectedImage.name}</span>
            </div>
          )}
          <div style={styles.inputRow}>
            <div style={styles.inputControls}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                style={{ display: 'none' }}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={styles.attachButton}
                title="Attach image"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </button>
            </div>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="บอกปัญหาของคุณให้ AI ช่วยแก้ไข..."
              style={styles.messageInput}
              disabled={loading}
              tabIndex={4}
              rows={1}
            />
            <button
              type="submit"
              disabled={loading || (!inputValue.trim() && !selectedImage)}
              style={{
                ...styles.sendButton,
                opacity: loading || (!inputValue.trim() && !selectedImage) ? 0.5 : 1,
              }}
              title="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.6915026,12.4744748 L3.50612381,13.2599618 C3.19218622,13.2599618 3.03521743,13.4170592 3.03521743,13.5741566 L1.15159189,20.0151496 C0.8376543,20.8006365 0.99,21.89 1.77946707,22.52 C2.41,22.99 3.50612381,23.1 4.13399899,22.8429026 L21.714504,14.0454487 C22.6563168,13.5741566 23.1272231,12.6315722 22.9702544,11.6889879 L4.13399899,1.16151496 C3.34915502,0.9 2.40734225,1.00636533 1.77946707,1.4776575 C0.994623095,2.10604706 0.837654326,3.0486314 1.15159189,3.99021575 L3.03521743,10.4312088 C3.03521743,10.5883061 3.19218622,10.7454035 3.50612381,10.7454035 L16.6915026,11.5308905 C16.6915026,11.5308905 17.1624089,11.5308905 17.1624089,12.0021827 C17.1624089,12.4744748 16.6915026,12.4744748 16.6915026,12.4744748 Z"/>
              </svg>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    background: 'linear-gradient(135deg, #fafbfc 0%, #f0f4ff 50%, #faf8ff 100%)',
    pointerEvents: 'auto',
    paddingBottom: '74px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  // Header Styles
  header: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 50%, #ffffff 100%)',
    borderBottom: '2px solid rgba(30, 58, 138, 0.12)',
    padding: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 8px 32px rgba(30, 58, 138, 0.12), 0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  headerLeft: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#1e3a8a',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '700',
    pointerEvents: 'auto',
    padding: '0.5rem',
    transition: 'all 0.2s',
  },
  'backBtn:hover': {
    color: '#1e3a8a',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  ticketInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  ticketNumber: {
    fontSize: '1.25rem',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #0f2847 50%, #f97316 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  ticketStatus: {
    fontSize: '0.875rem',
    color: '#64748b',
    margin: 0,
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    fontWeight: '600',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.375rem 0.875rem',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    color: '#1e3a8a',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  priority: {
    fontSize: '0.75rem',
    color: '#dc2626',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  agentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    paddingLeft: '1rem',
    borderLeft: '2px solid rgba(30, 58, 138, 0.2)',
  },
  agentAvatar: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e3a8a 100%)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '0.875rem',
    boxShadow: '0 6px 20px rgba(30, 58, 138, 0.3)',
  },
  agentName: {
    fontSize: '0.875rem',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  agentStatus: {
    fontSize: '0.75rem',
    color: '#10b981',
    margin: 0,
    fontWeight: '600',
  },

  // Messages Container Styles
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '1.5rem',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  emptyIcon: {
    fontSize: '3.5rem',
    opacity: 0.3,
    margin: 0,
  },
  emptyTitle: {
    fontSize: '1.125rem',
    color: '#94a3b8',
    margin: '1rem 0 0 0',
    fontWeight: '600',
  },
  dateSeperator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '1.5rem 0',
  },
  'dateSeperator span': {
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
    padding: '0.375rem 1rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    color: '#64748b',
    fontWeight: '700',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '0.75rem',
    marginBottom: '0.5rem',
    animation: 'slideIn 0.3s ease-out',
  },
  agentAvatarSmall: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: '700',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  messageBubble: {
    maxWidth: '65%',
    padding: '0.875rem 1.125rem',
    borderRadius: '16px',
    wordWrap: 'break-word',
    pointerEvents: 'auto',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
  },
  bubbleOwn: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e3a8a 100%)',
    color: '#ffffff',
    borderBottomRightRadius: '4px',
  },
  bubbleOther: {
    backgroundColor: '#ffffff',
    color: '#1f2937',
    borderLeft: '4px solid #f97316',
    borderBottomLeftRadius: '4px',
  },
  senderName: {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: '#374151',
    marginBottom: '0.5rem',
    opacity: 0.8,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  messageContent: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    margin: '0.25rem 0',
    wordWrap: 'break-word',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  },
  messageImage: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    maxHeight: '300px',
  },
  messageFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginTop: '0.5rem',
    fontSize: '0.7rem',
    opacity: 0.7,
  },
  messageTime: {
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  readStatus: {
    fontSize: '0.65rem',
  },

  // Input Area Styles
  inputContainer: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 50%, #ffffff 100%)',
    padding: '0.875rem 1.25rem',
    boxShadow: '0 -4px 16px rgba(30, 58, 138, 0.1)',
    flexShrink: 0,
  },
  inputAreaWrapper: {
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  imagePreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid rgba(30, 58, 138, 0.2)',
  },
  imagePreviewItem: {
    position: 'relative',
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  previewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeImageBtn: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    fontWeight: 'bold',
  },
  imageFileName: {
    fontSize: '0.8rem',
    color: '#64748b',
    fontWeight: '600',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  inputControls: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    height: '44px',
  },
  attachButton: {
    background: 'linear-gradient(135deg, #f97316 0%, #f97316 100%)',
    border: 'none',
    padding: '0.75rem',
    borderRadius: '12px',
    cursor: 'pointer',
    pointerEvents: 'auto',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)',
    flexShrink: 0,
  },
  'attachButton:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 25px rgba(249, 115, 22, 0.4)',
  },
  'attachButton:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  messageInput: {
    flex: 1,
    padding: '0.75rem 1rem',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '0.95rem',
    outline: 'none',
    fontFamily: 'inherit',
    pointerEvents: 'auto',
    backgroundColor: '#ffffff',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: '500',
    resize: 'none',
    height: '44px',
    color: '#1e293b',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
    lineHeight: '1.5',
  },
  'messageInput:focus': {
    borderColor: '#1e3a8a',
    boxShadow: '0 0 0 3px rgba(30, 58, 138, 0.1), 0 4px 12px rgba(30, 58, 138, 0.25)',
    backgroundColor: 'rgba(255, 255, 255, 1)',
    height: '44px',
  },
  'messageInput:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  sendButton: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #0f2847 100%)',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'auto',
    width: '44px',
    height: '44px',
    whiteSpace: 'nowrap',
    boxShadow: '0 6px 20px rgba(30, 58, 138, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  'sendButton:hover:not(:disabled)': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(30, 58, 138, 0.4)',
  },
  'sendButton:active:not(:disabled)': {
    transform: 'translateY(0px)',
  },
  'sendButton:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  // Button Group Styles
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '1rem',
    width: '100%',
  },
  quickButton: {
    backgroundColor: 'rgba(30, 58, 138, 0.1)',
    border: '2px solid rgba(30, 58, 138, 0.6)',
    color: '#1e3a8a',
    padding: '0.75rem 1rem',
    borderRadius: '10px',
    fontSize: '0.85rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'auto',
    textAlign: 'center',
    whiteSpace: 'normal',
    overflow: 'visible',
    width: '100%',
    minHeight: 'auto',
    lineHeight: '1.4',
  },
  'quickButton:hover': {
    backgroundColor: '#dbeafe',
    borderColor: '#1e3a8a',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(30, 58, 138, 0.25)',
  },
  'quickButton:active': {
    transform: 'translateY(0px)',
  },
};

